import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RoleBadge } from '../RoleBadge';
import { Button } from '../ui/button';
import { LogOut, Menu, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UniversalSearch } from '../UniversalSearch';

// Motivational quotes that rotate
const motivationalQuotes = [
  "Every call brings you closer to your goal!",
  "Consistency beats talent when talent doesn't work hard.",
  "Your next recruit could change everything!",
  "Sprint to District - You've got this!",
  "50 messages a day keeps the competition away!",
  "Leaders are made, not born. Keep growing!",
  "Today's effort is tomorrow's success.",
  "The fortune is in the follow-up!",
];

export const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [quote, setQuote] = useState('');

  useEffect(() => {
    // Random quote on load
    setQuote(motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]);
    
    // Rotate quote every 30 seconds
    const interval = setInterval(() => {
      setQuote(motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <header data-testid="app-header" className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuClick}
            className="lg:hidden"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden md:block">
            <h2 className="text-lg font-semibold text-[#333333]">
              Welcome, {user?.first_name} {user?.last_name}
            </h2>
            <p className="text-xs text-[#E31837] italic animate-pulse">{quote}</p>
          </div>
          <div className="md:hidden">
            <h2 className="text-base font-semibold text-[#333333]">
              Hi, {user?.first_name}!
            </h2>
          </div>
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          {/* Universal Search Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
            data-testid="search-button"
          >
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">Search</span>
            <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded border bg-gray-100 px-1.5 font-mono text-xs text-gray-500">
              ⌘K
            </kbd>
          </Button>
          
          <RoleBadge role={user?.role} />
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {/* Universal Search Modal */}
      <UniversalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};
